import { editorUrl } from '@/lib/editorBasePath';
import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { YearbookPage, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import { ChevronLeft, ChevronRight, Loader2, Check, LayoutGrid, Users } from 'lucide-react';
import TrombiPreview from './TrombiPreview';

export interface TrombiAdditionalObject {
  width: number;
  height: number;
  x: number;
  y: number;
  angle?: number;
  object_type: string;
  image_url?: string;
  thumbnail?: string;
  color?: string;
  shape_type?: string;
  content?: string;
  fontSize?: string;
  fontFamily?: string;
  justify?: string;
  force_dimension?: boolean;
  send_to_back?: boolean;
  bring_to_front?: boolean;
  picture_template_url?: string;
  zIndex?: number | string;
}

export interface TrombiDispositionShadow {
  offsetX: number;
  offsetY: number;
  blur: number;
  color: string;
}

export interface TrombiDisposition {
  width: number;
  height: number;
  x: number;
  y: number;
  angle?: number;
  background_color?: string;
  borderColor?: string;
  shadow?: TrombiDispositionShadow;
  additional_objects?: TrombiAdditionalObject[];
  additionnal_objects?: TrombiAdditionalObject[];
  zIndex?: number | string;
}

export interface TrombiObjectPosition {
  width: number;
  height: number;
  x: number;
  y: number;
  angle?: number;
  object_type: string;
  content?: string;
  fontSize?: string;
  fontFamily?: string;
  justify?: string;
  color?: string;
  backgroundColor?: string;
  rounded?: boolean;
  zIndex?: number | string;
}

export interface TrombiTitlePosition {
  width: number;
  height: number;
  x: number;
  y: number;
  angle?: number;
  object_type: string;
  content?: string;
  fontSize?: string;
  fontFamily?: string;
  justify?: string;
  color?: string;
  backgroundColor?: string;
  zIndex?: number | string;
}

export interface TrombiTemplate {
  id: number;
  style_type: number;
  preview?: string;
  preview_img?: string;
  background_color?: string;
  background?: { image_url: string };
  title?: TrombiTitlePosition;
  title2?: TrombiTitlePosition;
  objects_positions: TrombiObjectPosition[];
  objects_positions_name?: TrombiObjectPosition[] | TrombiObjectPosition[][];
  objects_positions_picture?: TrombiObjectPosition[] | TrombiObjectPosition[][];
  dispositions: TrombiDisposition[] | TrombiDisposition[][];
  additionnal_objects?: TrombiAdditionalObject[];
  additional_objects?: TrombiAdditionalObject[];
  page_pair?: boolean;
  personn_per_page?: number;
  multi_objects_positions?: boolean;
  switch_index?: number;
  [key: `objects_positions${number}`]: TrombiObjectPosition[];
}

type DialogMode = 'choose' | 'all_models' | 'by_count';

interface TrombiPlaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionId: number;
  sectionName: string;
  studentCount: number;
  pages: YearbookPage[];
  onTrombiCreated: (data: Record<string, { objects_positions: any[] }>) => void;
}

const TrombiPlaceDialog: React.FC<TrombiPlaceDialogProps> = ({
  open,
  onOpenChange,
  sectionId,
  sectionName,
  studentCount,
  pages,
  onTrombiCreated,
}) => {
  const [mode, setMode] = useState<DialogMode>('choose');
  const [step, setStep] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState<number | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [selectedPageIndex, setSelectedPageIndex] = useState<number | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [trombiDesigns, setTrombiDesigns] = useState<TrombiTemplate[]>([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  // all_models mode state
  const [allModelsData, setAllModelsData] = useState<Record<string, TrombiTemplate[]>>({});
  const [loadingAllModels, setLoadingAllModels] = useState(false);
  const [allModelsStep, setAllModelsStep] = useState(1); // 1=choose template, 2=choose page

  useEffect(() => {
    if (open) {
      setMode('choose');
      setStep(1);
      setSelectedPerPage(null);
      setSelectedStyle(null);
      setSelectedPageIndex(null);
      setSelectedTemplateId(null);
      setIsCreating(false);
      setTrombiDesigns([]);
      setAllModelsData({});
      setAllModelsStep(1);
    }
  }, [open]);

  // Fetch all models for section (used by both modes)
  useEffect(() => {
    if ((mode === 'all_models' || mode === 'by_count') && Object.keys(allModelsData).length === 0 && !loadingAllModels) {
      setLoadingAllModels(true);
      fetch(editorUrl(`/load_trombis_for_section?section_id=${sectionId}`))
        .then((res) => res.json())
        .then((data) => {
          const cleaned: Record<string, TrombiTemplate[]> = {};
          for (const [key, val] of Object.entries(data ?? {})) {
            if (Array.isArray(val)) cleaned[key] = val as TrombiTemplate[];
          }
          setAllModelsData(cleaned);
        })
        .catch((err) => console.error('Failed to load trombi models:', err))
        .finally(() => setLoadingAllModels(false));
    }
  }, [mode, sectionId]);

  // Fetch designs after step 3 (design selection) in by_count mode
  useEffect(() => {
    if (mode === 'by_count' && step === 3 && selectedPerPage) {
      // Use allModelsData filtered by selectedPerPage
      const templates = allModelsData[String(selectedPerPage)] ?? [];
      setTrombiDesigns(templates);
    }
  }, [mode, step, selectedPerPage, allModelsData]);

  const perPageOptions = useMemo(() => {
    return Object.keys(allModelsData).map(Number).sort((a, b) => b - a);
  }, [allModelsData]);

  // For all_models, derive personn_per_page from the selected template
  const allModelsSelectedPerPage = useMemo(() => {
    if (mode !== 'all_models' || !selectedTemplateId) return null;
    for (const [countStr, templates] of Object.entries(allModelsData)) {
      if (templates.some((t) => t.id === selectedTemplateId)) return Number(countStr);
    }
    return null;
  }, [mode, selectedTemplateId, allModelsData]);

  const effectivePerPage = mode === 'all_models' ? allModelsSelectedPerPage : selectedPerPage;

  // Find the selected template (to detect page_pair = double-page spreads)
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    for (const templates of Object.values(allModelsData)) {
      const found = templates.find((t) => t.id === selectedTemplateId);
      if (found) return found;
    }
    return trombiDesigns.find((t) => t.id === selectedTemplateId) ?? null;
  }, [selectedTemplateId, allModelsData, trombiDesigns]);

  const pagesNeeded = useMemo(() => {
    if (!effectivePerPage || studentCount <= 0) return 1;
    const units = Math.ceil(studentCount / effectivePerPage);
    // For page_pair templates, each "unit" occupies a double-page spread (2 pages).
    const pages = selectedTemplate?.page_pair ? units * 2 : units;
    return Math.max(1, pages);
  }, [studentCount, effectivePerPage, selectedTemplate]);

  const selectablePages = useMemo(
    () => pages.filter((p) => !p.coverType),
    [pages]
  );

  const handleCreate = async () => {
    if (!selectedTemplateId || selectedPageIndex === null || !effectivePerPage) return;
    setIsCreating(true);
    try {
      const res = await fetch(editorUrl('/create_trombi'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_id: sectionId,
          template_id: selectedTemplateId,
          page_id: selectablePages[selectedPageIndex]?.pageNumber,
          personn_per_page: effectivePerPage,
          nb_page: pagesNeeded,
        }),
      });
      const data = await res.json();
      onTrombiCreated(data);
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create trombi:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const stepTitles = ['Choix du nombre de personnes par page', 'Choix de la page', 'Style et disposition'];
  const allModelsStepTitles = ['Choix du modèle', 'Choix de la page'];

  const handleBack = () => {
    if (mode === 'all_models') {
      if (allModelsStep === 1) {
        setMode('choose');
      } else {
        setAllModelsStep((s) => s - 1);
      }
    } else if (mode === 'by_count' && step === 1) {
      setMode('choose');
    } else {
      setStep((s) => s - 1);
    }
  };

  const isChooseScreen = mode === 'choose';
  const currentStepLabel = mode === 'all_models'
    ? `${studentCount} élèves · Étape ${allModelsStep}/2 — ${allModelsStepTitles[allModelsStep - 1]}`
    : `${studentCount} élèves · Étape ${step}/3 — ${stepTitles[step - 1]}`;
  const totalSteps = mode === 'all_models' ? 2 : 3;
  const currentStep = mode === 'all_models' ? allModelsStep : step;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl flex flex-col max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="text-base">
            {isChooseScreen ? 'Ajout du trombi dans le yearbook' : `Placer « ${sectionName} »`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isChooseScreen
              ? `${studentCount} élèves — Choisissez une méthode`
              : currentStepLabel}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators — hidden on choose screen */}
        {!isChooseScreen && (
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  s <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto min-h-0">

          {/* Initial choice screen */}
          {isChooseScreen && (
            <div className="py-4 grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  setMode('all_models');
                }}
                className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-border bg-card hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm transition-all duration-150 group"
              >
                <LayoutGrid size={32} className="text-muted-foreground group-hover:text-primary/80 transition-colors" />
                <span className="text-sm font-semibold text-foreground group-hover:text-primary/80 transition-colors">
                  Voir tous les modèles
                </span>
                <span className="text-[11px] text-muted-foreground text-center leading-tight">
                  Parcourir l'ensemble des designs disponibles
                </span>
              </button>

              <button
                onClick={() => {
                  setMode('by_count');
                  setStep(1);
                }}
                className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-border bg-card hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm transition-all duration-150 group"
              >
                <Users size={32} className="text-muted-foreground group-hover:text-primary/80 transition-colors" />
                <span className="text-sm font-semibold text-foreground group-hover:text-primary/80 transition-colors">
                  Choix par nombre de personnes
                </span>
                <span className="text-[11px] text-muted-foreground text-center leading-tight">
                  Sélectionner le nombre de personnes par page puis le design
                </span>
              </button>
            </div>
          )}

          {/* Mode: all_models — 2-step flow */}
          {mode === 'all_models' && (
            <>
              {/* Step 1: Browse all templates grouped by person count */}
              {allModelsStep === 1 && (
                <div className="py-1 space-y-4">
                  {loadingAllModels ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="animate-spin text-muted-foreground" size={20} />
                    </div>
                  ) : (
                    Object.entries(allModelsData)
                      .sort(([a], [b]) => Number(b) - Number(a))
                      .map(([countStr, templates]) => {
                        const count = Number(countStr);
                        const units = Math.max(1, Math.ceil(studentCount / count));
                        const neededValues = Array.from(
                          new Set(templates.map((t) => (t.page_pair ? units * 2 : units)))
                        ).sort((a, b) => a - b);
                        const neededLabel =
                          neededValues.length > 1
                            ? `${neededValues[0]} ou ${neededValues[neededValues.length - 1]} pages utilisées`
                            : `${neededValues[0]} page${neededValues[0] > 1 ? 's' : ''} utilisée${neededValues[0] > 1 ? 's' : ''}`;
                        return (
                          <div key={countStr}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold text-foreground">
                                {count} personne{count > 1 ? 's' : ''} par page
                              </span>
                              <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                                {neededLabel} dans le Yearbook
                              </span>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                              {templates.map((tpl) => (
                                <button
                                  key={tpl.id}
                                  onClick={() => setSelectedTemplateId(tpl.id)}
                                  className={`rounded-lg border transition-colors overflow-hidden ${
                                    selectedTemplateId === tpl.id
                                      ? 'border-primary ring-2 ring-primary/20'
                                      : 'border-border hover:border-muted-foreground/30'
                                  }`}
                                  style={{ aspectRatio: `${PAGE_WIDTH}/${PAGE_HEIGHT}` }}
                                >
                                  <TrombiPreview template={tpl} />
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              )}

              {/* Step 2: Page selection */}
              {allModelsStep === 2 && (
                <div className="space-y-2 py-1">
                  <p className="text-xs text-muted-foreground">
                    Sur quelle page placer le trombinoscope ? ({pagesNeeded} page{pagesNeeded > 1 ? 's' : ''} nécessaire{pagesNeeded > 1 ? 's' : ''})
                  </p>
                  <div className="border rounded-lg divide-y">
                    {selectablePages.map((page, idx) => {
                      const isTrombi = page.itemType === 'Trombi';
                      return (
                        <button
                          key={page.id}
                          disabled={isTrombi}
                          onClick={() => !isTrombi && setSelectedPageIndex(idx)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                            isTrombi
                              ? 'opacity-40 cursor-not-allowed bg-muted/40'
                              : selectedPageIndex === idx
                              ? 'bg-primary/10 text-primary'
                              : 'hover:bg-muted'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            Page {page.pageNumber}
                            {isTrombi && (
                              <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                                Trombi
                              </span>
                            )}
                            {!isTrombi && (() => {
                              const photoCount = page.elements.filter(e => e.type === 'image').length;
                              return photoCount > 0 ? (
                                <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                                  {photoCount} photo{photoCount > 1 ? 's' : ''}
                                </span>
                              ) : null;
                            })()}
                          </span>
                          {selectedPageIndex === idx && <Check size={14} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Mode: by_count — existing 3-step flow */}
          {mode === 'by_count' && (
            <>
              {/* Step 1: Person per page */}
              {step === 1 && (
                <div className="py-2">
                  {loadingAllModels ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="animate-spin text-muted-foreground" size={20} />
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {perPageOptions.map((n) => {
                        const templates = allModelsData[String(n)] ?? [];
                        const units = Math.max(1, Math.ceil(studentCount / n));
                        const neededValues = Array.from(
                          new Set(templates.map((t) => (t.page_pair ? units * 2 : units)))
                        ).sort((a, b) => a - b);
                        const neededLabel =
                          neededValues.length > 1
                            ? `${neededValues[0]} ou ${neededValues[neededValues.length - 1]} pages`
                            : `${neededValues[0]} page${neededValues[0] > 1 ? 's' : ''} utilisée${neededValues[0] > 1 ? 's' : ''}`;
                        const isSelected = selectedPerPage === n;
                        return (
                          <button
                            key={n}
                            onClick={() => setSelectedPerPage(n)}
                            className={`relative flex flex-col items-center justify-center gap-1 p-5 rounded-xl border-2 text-center transition-all duration-150 group ${
                              isSelected
                                ? 'border-primary bg-primary/10 shadow-md shadow-primary/10'
                                : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm'
                            }`}
                          >
                            {isSelected && (
                              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary" />
                            )}
                            <span className={`text-4xl font-bold leading-none tabular-nums ${isSelected ? 'text-primary' : 'text-foreground group-hover:text-primary/80'}`}>
                              {n}
                            </span>
                            <span className={`text-[11px] font-medium leading-tight ${isSelected ? 'text-primary/80' : 'text-muted-foreground'}`}>
                              personne{n > 1 ? 's' : ''} par page
                            </span>
                            <div className={`mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary/80'
                            }`}>
                              {neededLabel}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Page selection */}
              {step === 2 && (
                <div className="space-y-2 py-1">
                  <p className="text-xs text-muted-foreground">
                    Sur quelle page placer le trombinoscope ? ({pagesNeeded} page{pagesNeeded > 1 ? 's' : ''} nécessaire{pagesNeeded > 1 ? 's' : ''})
                  </p>
                  <div className="border rounded-lg divide-y">
                    {selectablePages.map((page, idx) => {
                      const isTrombi = page.itemType === 'Trombi';
                      return (
                        <button
                          key={page.id}
                          disabled={isTrombi}
                          onClick={() => !isTrombi && setSelectedPageIndex(idx)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                            isTrombi
                              ? 'opacity-40 cursor-not-allowed bg-muted/40'
                              : selectedPageIndex === idx
                              ? 'bg-primary/10 text-primary'
                              : 'hover:bg-muted'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            Page {page.pageNumber}
                            {isTrombi && (
                              <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                                Trombi
                              </span>
                            )}
                            {!isTrombi && (() => {
                              const photoCount = page.elements.filter(e => e.type === 'image').length;
                              return photoCount > 0 ? (
                                <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                                  {photoCount} photo{photoCount > 1 ? 's' : ''}
                                </span>
                              ) : null;
                            })()}
                          </span>
                          {selectedPageIndex === idx && <Check size={14} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 3: Design selection with previews */}
              {step === 3 && (
                <div className="space-y-2 py-1">
                  {loadingDesigns ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="animate-spin text-muted-foreground" size={20} />
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {trombiDesigns.map((tpl) => (
                        <button
                          key={tpl.id}
                          onClick={() => setSelectedTemplateId(tpl.id)}
                          className={`rounded-lg border transition-colors overflow-hidden ${
                            selectedTemplateId === tpl.id
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-border hover:border-muted-foreground/30'
                          }`}
                          style={{ aspectRatio: `${PAGE_WIDTH}/${PAGE_HEIGHT}` }}
                        >
                          <TrombiPreview template={tpl} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </div>

        {/* Navigation buttons — hidden on choose screen */}
        {!isChooseScreen && (
          <div className="flex items-center justify-between pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBack}
              className="gap-1"
            >
              <ChevronLeft size={14} />
              Retour
            </Button>

            {mode === 'all_models' && allModelsStep === 1 ? (
              <Button
                size="sm"
                onClick={() => setAllModelsStep(2)}
                disabled={!selectedTemplateId}
                className="gap-1"
              >
                Suivant
                <ChevronRight size={14} />
              </Button>
            ) : mode === 'all_models' && allModelsStep === 2 ? (
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!selectedTemplateId || selectedPageIndex === null || isCreating}
                className="gap-1"
              >
                {isCreating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Création…
                  </>
                ) : (
                  'Créer'
                )}
              </Button>
            ) : mode === 'by_count' && step < 3 ? (
              <Button
                size="sm"
                onClick={() => setStep((s) => s + 1)}
                disabled={
                  (step === 1 && !selectedPerPage) ||
                  (step === 2 && selectedPageIndex === null)
                }
                className="gap-1"
              >
                Suivant
                <ChevronRight size={14} />
              </Button>
            ) : mode === 'by_count' && step === 3 ? (
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!selectedTemplateId || isCreating}
                className="gap-1"
              >
                {isCreating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Création…
                  </>
                ) : (
                  'Créer'
                )}
              </Button>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrombiPlaceDialog;
